import { useState } from "react";
import { MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { api, inr, formatApiError } from "../lib/api";
import { STATUS_META, fmtDay, daysText } from "../lib/reminders";

export const logReminder = async (r) => {
  const { data } = await api.post("/admin/reminders/log", { player_id: r.player.player_id, amount: r.total, status: r.status, cycle_key: r.cycle_key });
  return data;
};

export const SentCounter = ({ r, onLogged, testid }) => {
  const [busy, setBusy] = useState(false);
  const log = async () => {
    setBusy(true);
    try {
      const d = await logReminder(r);
      toast.success(d.duplicate ? "Already recorded a moment ago" : `Reminder #${d.reminders_sent} recorded for ${r.player.name}`);
      onLogged();
    } catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const n = r.reminders_sent || 0;
  return (
    <div className="flex items-center gap-2">
      <span className={`text-[11px] font-semibold ${n >= 4 ? "text-red-300" : n ? "text-[#D4AF37]" : "text-white/40"}`} data-testid={`${testid}-count`}>{n} sent</span>
      <button onClick={log} disabled={busy || !r.cycle_key} title="Record that you sent a reminder"
        className="flex items-center gap-1 border border-white/20 text-white/70 text-[10px] font-bold uppercase tracking-widest px-2.5 py-2 rounded-sm hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors duration-200 disabled:opacity-30"
        data-testid={`${testid}-log`}><Send size={12} /> +1 Sent</button>
    </div>
  );
};

export const ReminderRow = ({ r, onPreview, onLogged }) => {
  const meta = STATUS_META[r.status];
  const pid = r.player.player_id;
  const noDues = r.status === "no_dues";
  return (
    <div className="border border-white/10 bg-[#0A0A0A] p-4 sm:p-5 grid grid-cols-2 sm:grid-cols-12 gap-3 items-center hover:border-white/20 transition-colors duration-200" data-testid={`reminder-row-${pid}`}>
      <div className="col-span-2 sm:col-span-3 min-w-0">
        <p className="font-semibold text-sm truncate">{r.player.name} <span className="text-[#D4AF37] text-xs ml-1">{pid}</span></p>
        <p className="text-white/40 text-xs mt-0.5 truncate">{r.player.centre || "No centre"} · {r.player.batch || "No batch"}</p>
        <p className="text-white/50 text-[11px] mt-1" data-testid={`reminder-package-${pid}`}>{r.package_label}</p>
      </div>
      <div className="sm:col-span-2">
        <p className="text-white/40 text-[10px] uppercase tracking-widest">{noDues ? "Next Due" : "Due Date"}</p>
        <p className="text-sm font-semibold" data-testid={`reminder-due-${pid}`}>{fmtDay(r.due)}</p>
        <p className={`text-[11px] ${r.days > 0 ? "text-red-300" : "text-white/50"}`} data-testid={`reminder-days-${pid}`}>{daysText(r.days)}</p>
      </div>
      <div className="sm:col-span-2 text-right sm:text-left">
        <p className="text-white/40 text-[10px] uppercase tracking-widest">Outstanding</p>
        <p className="font-heading text-lg font-black text-[#D4AF37]" data-testid={`reminder-total-${pid}`}>{inr(r.total)}</p>
        {!noDues && <p className="text-white/40 text-[11px]" data-testid={`reminder-split-${pid}`}>{inr(r.dueNow)} due now{r.upcoming > 0 ? ` · ${inr(r.upcoming)} upcoming` : ""}</p>}
      </div>
      <div className="sm:col-span-2">
        <span className={`inline-block border px-2 py-1 text-[10px] uppercase tracking-widest ${meta.cls}`} data-testid={`reminder-status-${pid}`}>{meta.label}</span>
        <p className="text-white/40 text-[11px] mt-1.5" data-testid={`reminder-last-${pid}`}>
          {r.last_reminder ? `Last sent ${fmtDay(r.last_reminder.date)}` : "No reminder sent"}
        </p>
      </div>
      <div className="col-span-2 sm:col-span-3 flex flex-wrap items-center gap-3 sm:justify-end">
        {!noDues && <SentCounter r={r} onLogged={onLogged} testid={`reminder-sent-${pid}`} />}
        {!noDues && (
          <button onClick={() => onPreview(r)} className="flex items-center gap-2 border border-[#D4AF37] text-[#D4AF37] text-[11px] font-bold uppercase tracking-widest px-3 py-2.5 rounded-sm hover:bg-[#D4AF37] hover:text-black transition-colors duration-200" data-testid={`reminder-preview-${pid}`}>
            <MessageCircle size={14} /> Preview
          </button>
        )}
      </div>
    </div>
  );
};
